import React, { useContext, useEffect, useRef, useState } from "react";
import { findLinkByAlias } from "../../../../helpers/GiftCardHelper";
import { formatStringWithPlaceholders, isNotNullOrEmpty } from "../../../../helpers/Utils";
import { CheckoutBillingAddressData } from "../../../../models/ElavonCheckoutModels";
import { CheckoutStatusEnum } from "../../../../models/GiftCardCheckoutResult.model";
import { IInvoiceDetails } from "../../../../models/InvoiceDetails.model";
import { ICheckoutSessionData } from "../../../../models/PaymentStep.model";
import { IBuyGiftCardModel } from "../../../../models/buy-giftcard/BuyGiftCard.model";
import { IReviewAndPayPaymentStepModel, ReviewAndPayPaymentStepModel } from "../../../../models/buy-giftcard/review-pay/ReviewAndPayPaymentStep.model";
import { BuyGiftCardStateEnum } from "../../../../models/enums/BuyGiftCardStateEnum";
import { BuyGiftCardSubpageEnum } from "../../../../models/enums/BuyGiftCardSubpageEnum";
import { PaymentTypeEnum } from "../../../../models/enums/PaymentTypeEnum";
import { IBuyGiftCardStepProps } from "../../../../models/interfaces/BuyGiftCardStep.interface";
import DataService from "../../../../services/DataService.service";
import { AppSettingContext } from "../../../Application";
import LoadingComponent from "../../../shared/LoadingComponent";
import ElavonPayment, { HandleElavonCheckout, HandleThreeDSecureAuth } from "../../shared/ElavonPayment";
import InvoiceDetails from "../../shared/InvoiceDetails";

interface IReviewAndPayPaymentDetails {
    totalAmount: number;
    senderInfo: {
        isBusinessBuyer: boolean;
        fullName: string;
        email: string;
        companyName: string;
    };
}

interface IReviewAndPayPaymentStepProps extends IBuyGiftCardStepProps {
    stepModel: IReviewAndPayPaymentStepModel;
    paymentDetails: IReviewAndPayPaymentDetails;
    isAvailable: boolean;

    getPageModel(): IBuyGiftCardModel;
    handleError(message: string): void;
    handleStateChange(newSubpage: BuyGiftCardSubpageEnum, newState: BuyGiftCardStateEnum): void;
    handleSubmit(model: IReviewAndPayPaymentStepModel): void;
}

const ReviewAndPayPaymentStep = (props: IReviewAndPayPaymentStepProps) => {
    const { labels, elavonSettings, links, cardSettings } = useContext(AppSettingContext);

    const checkboxRef = useRef(null);
    const checkboxPaymentFormCompleteRef = useRef(null);
    const termsPageUrl = findLinkByAlias("terms-page", links);
    const culture = document.documentElement.lang ?? "en-GB";
    const basketId = (props.getPageModel()).id;
    const errQueryParamKey = 'sp-err-code';

    //#region STATE PROPERTIES

    const [stepModel, setStepModel] = useState<IReviewAndPayPaymentStepModel>(props.stepModel);
    const [isModelValid, setIsModelValid] = useState<boolean>();
    const [isLoading, setIsLoading] = useState(true);

    const [isCardPaymentLoading, setIsCardPaymentLoading] = useState(false);
    const [isPaymentSaving, setIsPaymentSaving] = useState(false);
    const [checkoutBillingDetails, setCheckoutBillingDetails] = useState<CheckoutBillingAddressData>();

    //#endregion

    //#region USE EFFECT HOOKS

    useEffect(() => {
        setIsLoading(true);

        if (!elavonSettings.showCheckoutForm) {
            setStepModel({
                ...stepModel,
                paymentType: PaymentTypeEnum.BANK
            });

            return;
        }

        if (props.activeStep === BuyGiftCardStateEnum.REVIEW_PAY_PAYMENT) {
            handlePaymentStepInitialization();
        } else {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has(errQueryParamKey)) {
                urlParams.delete(errQueryParamKey);

                const updatedQueryParams = urlParams.size > 0 ? `?${urlParams.toString()}` : "";
                window.history.replaceState({}, '', `${window.location.pathname}${updatedQueryParams}`);
            }
        }
    }, []);

    useEffect(() => {
        setIsModelValid(isValid());

        setTimeout(() => {
            setIsCardPaymentLoading(false);
        }, 300);
    }, [stepModel]);

    useEffect(() => {
        setIsLoading(props.isSaving);
    }, [props.isSaving]);

    useEffect(() => {
        checkboxRef.current.checked = props.activeStep === BuyGiftCardStateEnum.REVIEW_PAY_PAYMENT;

        if (props.activeStep !== BuyGiftCardStateEnum.REVIEW_PAY_PAYMENT) {
            setStepModel({
                ...stepModel,
                termsAccepted: false
            });
        }
    }, [props.activeStep]);

    //#endregion

    //#region HANDLERS

    const handleExpandBoxCheck = (): void => {
        if (props.isAvailable) {
            if (checkboxRef.current.checked && props.activeStep !== BuyGiftCardStateEnum.REVIEW_PAY_PAYMENT) {
                props.handleSetActiveStep(BuyGiftCardStateEnum.REVIEW_PAY_PAYMENT);
            }
        }
    };

    const handlePaymentTypeChange = async (paymentType: PaymentTypeEnum): Promise<void> => {
        setIsCardPaymentLoading(true);
        const currPaymentDetails = stepModel.paymentDetails;

        if (paymentType === PaymentTypeEnum.CREDIT_CARD) {
            const checkoutSession = await createNewCheckoutSession(false);
            currPaymentDetails.checkoutKey = checkoutSession.sessionKey;
            currPaymentDetails.checkoutExpiry = checkoutSession.expiry;
        }

        setStepModel({
            ...stepModel,
            paymentType: paymentType,
            paymentDetails: currPaymentDetails
        });
    };

    const handlePaymentStepInitialization = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const spErrQueryParam = urlParams.get(errQueryParamKey);
        const newStepModel = { ...stepModel };

        if (spErrQueryParam) {
            newStepModel.paymentType = PaymentTypeEnum.CREDIT_CARD;
        }

        setStepModel(newStepModel);

        setTimeout(() => {
            if (spErrQueryParam) {
                handlePaymentTypeChange(PaymentTypeEnum.CREDIT_CARD);

                setTimeout(() => {
                    let errMsg = labels.get(`GiftCard.App.Steps.PreviewAndPay.Card.Errors.ThreeDForm${spErrQueryParam}`);
                    if (!errMsg) {
                        errMsg = labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Errors.ThreeDForm2")
                    }

                    switch (spErrQueryParam) {
                        case "1": console.error(`3D secure authentication returned status, other than 'Ok'.\r\n${errMsg}`); break;
                        case "2": console.error(`Some of the parameters sent to GiftCardService 3D authentication method were invalid.\r\n${errMsg}`); break;
                        case "3": console.error(`Services API returned BadRequest.\r\n${errMsg}`); break;
                        case "4": console.error(`Unauthorized access to Services API 3D secure endpoint.\r\n${errMsg}`); break;
                        case "5": console.error(`Services API returned invalid response.\r\n${errMsg}`); break;
                        case "6": console.error(`An unhandled error occurred processing 3D authentication response.\r\n${errMsg}`); break;
                        default: break;
                    }

                    displayError(errMsg);
                }, 500);

                const checkoutErrorDiv = document.querySelector("label[for=credit-debit-card]");
                if (checkoutErrorDiv) {
                    checkoutErrorDiv.scrollIntoView();
                }
            }
        }, 600);
    };

    const handleCreditCardFormUpdate = (isFormValid: boolean, billingDetails: CheckoutBillingAddressData): void => {
        const paymentCompletedChk = document.getElementById('chkPaymentCompleted') as HTMLInputElement;

        if (paymentCompletedChk) {
            paymentCompletedChk.checked = !isFormValid;
            paymentCompletedChk.click();
        }

        if (isFormValid) {
            setCheckoutBillingDetails(billingDetails);
        }
    };

    const handleInvoiceUpdate = (invoiceDetails: IInvoiceDetails): void => {
        if (invoiceDetails) {
            setStepModel({
                ...stepModel,
                invoiceDetails: invoiceDetails
            });
        }
    };

    const handleTermsAcceptedChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setStepModel({
            ...stepModel,
            termsAccepted: event.target.checked
        });
    };

    const handleGoBack = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.preventDefault();
        props.handleStateChange(BuyGiftCardSubpageEnum.CUSTOMISE_CARD, BuyGiftCardStateEnum.CUSTOMISE_MAIL);
    };

    const handleSaveAndContinue = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
        event.preventDefault();

        if (stepModel.paymentType === PaymentTypeEnum.CREDIT_CARD) {
            if (stepModel.paymentCompleted) {
                const paymentForm = document.getElementById('checkout-form') as HTMLFormElement;
                if (!paymentForm) {
                    displayError("Unable to find a payment form. Please refresh the page.");
                    return;
                }

                toggleFormVisibility(false);

                if (new Date(stepModel.paymentDetails.checkoutExpiry) <= new Date()) {
                    await createNewCheckoutSession(true);
                }

                HandleElavonCheckout({
                    sessionKey: stepModel.paymentDetails.checkoutKey,
                    expiry: stepModel.paymentDetails.checkoutExpiry
                });

                setTimeout(async () => {
                    await creditCardProcessPayment();
                }, 600);
            }
        } else if (stepModel.paymentType === PaymentTypeEnum.BANK) {
            setIsPaymentSaving(true);
            props.handleSubmit(stepModel);

            setTimeout(() => {
                setIsPaymentSaving(false);
            }, 3000);
        }
    };

    //#endregion

    //#region COMMON HELPERS

    const displayError = (errorMessage: string) => {
        const errorContainer = document.querySelector("#checkout-error") as HTMLDivElement;

        if (errorContainer) {
            errorContainer.style.display = "block";
            errorContainer.textContent = errorMessage;
        }
    };

    const toggleFormVisibility = (visible: boolean) => {
        const paymentBoxEl = document.getElementById("payment-box");
        if (paymentBoxEl) {
            paymentBoxEl.style.opacity = visible ? "1" : "0.2";
        }

        const paymentSubmitBtnEl = document.getElementById("paymentSubmitBtn") as HTMLButtonElement;
        if (paymentSubmitBtnEl) {
            paymentSubmitBtnEl.disabled = !visible;
        }
    };

    const getSenderDetailsForCreditCardPayment = () => {
        let firstName = "";
        let lastName = "";

        const spaceIdx = props.paymentDetails.senderInfo.fullName.indexOf(" ");
        if (spaceIdx === -1) {
            firstName = props.paymentDetails.senderInfo.fullName ?? "";
            lastName = "";
        } else {
            firstName = props.paymentDetails.senderInfo.fullName.substring(0, spaceIdx) ?? "";
            lastName = props.paymentDetails.senderInfo.fullName.substring(spaceIdx + 1) ?? "";
        }

        return {
            firstName: firstName,
            lastName: lastName,
            email: props.paymentDetails.senderInfo.email ?? "",
            phone: "",
        };
    };

    const createNewCheckoutSession = async (setSessionKeyModel: boolean): Promise<ICheckoutSessionData> => {
        const amount = props.paymentDetails.totalAmount;
        if (!amount || amount === 0) {
            console.error("An attempt to create new checkout session was made, but the checkout amount was invalid.");
            return;
        }

        if (!basketId) {
            console.error("An attempt to create new checkout session was made, but the basketId was invalid.")
            return;
        }

        const newMerchantSession = await DataService.getCheckoutData(basketId, amount, stepModel.paymentDetails.currency ?? "EUR");
        //console.log('Newly created merchant session: ', newMerchantSession);

        if (!setSessionKeyModel) {
            return newMerchantSession;
        }

        const newPaymentDetailsObj = stepModel.paymentDetails;
        newPaymentDetailsObj.checkoutKey = newMerchantSession.sessionKey;
        newPaymentDetailsObj.checkoutExpiry = newMerchantSession.expiry;

        setStepModel(currStepModel => ({
            ...currStepModel,
            paymentDetails: newPaymentDetailsObj
        }));

        return newMerchantSession;
    };

    const creditCardProcessPayment = async () => {
        const cardIdentifierField = document.querySelector("#cardIdentifier-field") as HTMLInputElement;
        const cardIdentifier = cardIdentifierField.value;

        if (!cardIdentifier) {
            console.error("Empty card identifier returned from SagePay API.");
            displayError(labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Errors.SagePay"));

            await createNewCheckoutSession(true);
            toggleFormVisibility(true);
            return;
        }

        console.log(`%cSuccessfully tokenised the card details\r\nCard identifier: ${cardIdentifier}`, 'color: green; font-weight: bold;');
        setIsPaymentSaving(true);

        const pageModel = props.getPageModel();
        pageModel.reviewAndPaySubpage.reviewAndPayPayment.paymentDetails.checkoutKey = stepModel.paymentDetails.checkoutKey;
        pageModel.reviewAndPaySubpage.reviewAndPayPayment.paymentDetails.checkoutExpiry = stepModel.paymentDetails.checkoutExpiry;
        DataService.updateBuyGiftCardModelCache(pageModel, culture)
            .then(async (response) => {
                if (response) {
                    const checkoutRes = await DataService.submitCreditCardPayment('Buy', basketId, stepModel.paymentDetails.checkoutKey, cardIdentifier, checkoutBillingDetails);
                    if (checkoutRes) {
                        if (checkoutRes.checkoutStatus == CheckoutStatusEnum.Success) {
                            if (isNotNullOrEmpty(checkoutRes.thankYouPageUrl)) {
                                window.location.href = checkoutRes.thankYouPageUrl;
                                return;
                            } else {
                                console.error("Invalid ThankYou page url provided");
                                window.location.href = "/";
                            }
                        } else if (checkoutRes.checkoutStatus == CheckoutStatusEnum.Secure3DAuth) {
                            const handleThreeDError = async (errorMessage: string) => {
                                const baseErrorMessage = labels.get("GiftCard.App.Steps.PreviewAndPay.Card.Errors.ThreeDForm0");

                                console.error(`${baseErrorMessage} ${errorMessage}`);
                                displayError(baseErrorMessage);
                                await createNewCheckoutSession(true);
                                toggleFormVisibility(true);
                                setIsPaymentSaving(false);
                            };

                            HandleThreeDSecureAuth(checkoutRes, handleThreeDError);
                            return;
                        } else if (checkoutRes.checkoutStatus == CheckoutStatusEnum.Failed || checkoutRes.checkoutStatus == CheckoutStatusEnum.Rejected) {
                            displayError(checkoutRes.statusMessage);
                            await createNewCheckoutSession(true);
                            toggleFormVisibility(true);
                            setIsPaymentSaving(false);
                            return;
                        }
                    }

                    setTimeout(() => {
                        setIsPaymentSaving(false);
                        toggleFormVisibility(true);
                    }, 1000);
                } else {
                    setIsPaymentSaving(false);
                    props.handleError("Unable to update basket cache");
                    console.error("Unable to update basket cache while checking out. Received an invalid response.");
                }
            }).catch((error) => {
                setIsPaymentSaving(false);
                props.handleError(error);
                console.error(error);
            });
    };

    const isValid = (): boolean => {
        if (props.paymentDetails.senderInfo.isBusinessBuyer && stepModel.paymentType === PaymentTypeEnum.BANK) {
            if (!stepModel.invoiceDetails.companyName || !stepModel.invoiceDetails.companyName.trim()) {
                return false;
            }
        }

        return ReviewAndPayPaymentStepModel.isValid(stepModel);
    };

    //#endregion

    return (
        <div className="box payment-type">
            <input
                type="checkbox"
                name="payment-type"
                id="payment-type"
                ref={checkboxRef}
                onChange={handleExpandBoxCheck}
                defaultChecked={false}
            />
            <div className="action-card">
                <h4 className="box-heading">
                    <i className="box-icon">
                        <img src="/static/options-card-page/options-card-icon-09.png" alt="icon 09" />
                    </i>
                    {labels.get("GiftCard.App.V2.ReviewAndPayPayment.Title")}
                    <i
                        className="box-arrow"
                        style={{ cursor: props.isAvailable ? "pointer" : "default" }}
                        onClick={() => {
                            if (props.isAvailable) {
                                checkboxRef.current.checked = !checkboxRef.current.checked;
                                handleExpandBoxCheck();
                            }
                        }}
                    ></i>
                </h4>

                {/* In case post basket request takes longer to load, display a loader */}
                {props.activeStep === BuyGiftCardStateEnum.REVIEW_PAY_WHEN_TO_SEND && isLoading && (
                    <LoadingComponent />
                )}

                {props.activeStep === BuyGiftCardStateEnum.REVIEW_PAY_PAYMENT && (
                    <div className="show">
                        {isLoading ? (
                            <LoadingComponent />
                        ) : (
                            <div id="payment-box" className="slide-content">
                                {elavonSettings.showCheckoutForm && props.paymentDetails.totalAmount <= (cardSettings.cardPaymentValueMax ?? 5000) && (
                                    <input
                                        type="radio"
                                        name="payment-type-services"
                                        id="credit-debit-card"
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                handlePaymentTypeChange(PaymentTypeEnum.CREDIT_CARD);
                                            }
                                        }}
                                        checked={stepModel.paymentType === PaymentTypeEnum.CREDIT_CARD}
                                    />
                                )}

                                <input
                                    type="radio"
                                    name="payment-type-services"
                                    id="bank-transfer"
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            handlePaymentTypeChange(PaymentTypeEnum.BANK);
                                        }
                                    }}
                                    checked={stepModel.paymentType === PaymentTypeEnum.BANK}
                                />

                                {isCardPaymentLoading ? (
                                    <LoadingComponent />
                                ) : (
                                    <>
                                        <div className="services flex-container">
                                            {elavonSettings.showCheckoutForm && props.paymentDetails.totalAmount <= (cardSettings.cardPaymentValueMax ?? 5000) ? (
                                                <label htmlFor="credit-debit-card">
                                                    <svg width="32" height="24" viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M4 0C1.79086 0 0 1.79086 0 4V6H32V4C32 1.79086 30.2091 0 28 0H4Z" fill="#6B7280" />
                                                        <path fillRule="evenodd" clipRule="evenodd" d="M32 10H0V20C0 22.2091 1.79086 24 4 24H28C30.2091 24 32 22.2091 32 20V10ZM4 18C4 16.8954 4.89543 16 6 16H8C9.10457 16 10 16.8954 10 18C10 19.1046 9.10457 20 8 20H6C4.89543 20 4 19.1046 4 18ZM14 16C12.8954 16 12 16.8954 12 18C12 19.1046 12.8954 20 14 20H16C17.1046 20 18 19.1046 18 18C18 16.8954 17.1046 16 16 16H14Z" fill="#6B7280" />
                                                    </svg>
                                                    {labels.get("GiftCard.App.V2.ReviewAndPayPayment.Label.CreditCard")}
                                                </label>
                                            ) : (
                                                <label style={{ display: 'none' }}></label>
                                            )}

                                            <label htmlFor="bank-transfer">
                                                <svg width="26" height="32" viewBox="0 0 26 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M7.99968 16H17.9997M7.99968 22.6667H17.9997M21.333 31H4.66634C2.82539 31 1.33301 29.5076 1.33301 27.6667V4.33333C1.33301 2.49238 2.82539 1 4.66634 1H13.976C14.418 1 14.8419 1.17559 15.1545 1.48816L24.1782 10.5118C24.4908 10.8244 24.6663 11.2483 24.6663 11.6904V27.6667C24.6663 29.5076 23.174 31 21.333 31Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                {labels.get("GiftCard.App.V2.ReviewAndPayPayment.Label.Invoice")}
                                            </label>
                                        </div>

                                        {(stepModel.paymentType === PaymentTypeEnum.CREDIT_CARD && elavonSettings.showCheckoutForm && props.paymentDetails.totalAmount <= (cardSettings.cardPaymentValueMax ?? 5000)) &&
                                            <ElavonPayment
                                                basketId={basketId ?? ""}
                                                senderDetails={getSenderDetailsForCreditCardPayment()}
                                                paymentData={{
                                                    amount: props.paymentDetails.totalAmount.toFixed(2) ?? "0",
                                                    currency: stepModel.paymentDetails.currency ?? "EUR"
                                                }}
                                                handleError={displayError}
                                                handleFormUpdate={handleCreditCardFormUpdate}
                                            />
                                        }

                                        {stepModel.paymentType === PaymentTypeEnum.BANK &&
                                            <InvoiceDetails
                                                isBusinessPurchase={props.paymentDetails.senderInfo.isBusinessBuyer}
                                                model={stepModel.invoiceDetails}
                                                senderDetails={{
                                                    fullName: props.paymentDetails.senderInfo.fullName ?? "",
                                                    email: props.paymentDetails.senderInfo.email ?? "",
                                                    emailIsValid: true,
                                                    company: props.paymentDetails.senderInfo.companyName ?? "",
                                                    phone: "",
                                                    firstName: "",
                                                    firstNameIsValid: true,
                                                    lastName: "",
                                                    lastNameIsValid: true,
                                                }}
                                                handleUpdate={handleInvoiceUpdate}
                                            />
                                        }
                                    </>
                                )}

                                <div id="checkout-error"></div>
                                <div className="flex-container form-field terms-conditions">
                                    <input
                                        type="checkbox"
                                        name="payment-type__accept"
                                        id="payment-type__accept"
                                        onChange={handleTermsAcceptedChange}
                                        defaultChecked={props.stepModel.termsAccepted}
                                    />
                                    <label htmlFor="payment-type__accept"
                                        dangerouslySetInnerHTML={{
                                            __html: formatStringWithPlaceholders(labels.get("GiftCard.App.V2.ReviewAndPayPayment.AcceptTerms"), termsPageUrl.url ?? "")
                                        }}
                                    />
                                </div>

                                {isPaymentSaving ? (
                                    <LoadingComponent />
                                ) : (
                                    <div className="buttons">
                                        <input
                                            type="checkbox"
                                            id="chkPaymentCompleted"
                                            ref={checkboxPaymentFormCompleteRef}

                                            onChange={(e) => {
                                                setStepModel({
                                                    ...stepModel,
                                                    paymentCompleted: checkboxPaymentFormCompleteRef.current.checked
                                                });
                                            }}
                                            defaultChecked={false}
                                            data-review-pay-box-hide
                                        />
                                        <button
                                            className="btn btn__full_width btn__secondary large-fs btn__large"
                                            onClick={handleGoBack}
                                        >
                                            {labels.get("GiftCard.App.V2.ReviewAndPayPayment.Button.GoBack")}
                                        </button>
                                        <button
                                            id="paymentSubmitBtn"
                                            className="btn btn__full_width btn__primary large-fs btn__large"
                                            onClick={handleSaveAndContinue}
                                            disabled={!isModelValid}
                                        >
                                            {formatStringWithPlaceholders(
                                                labels.get("GiftCard.App.V2.ReviewAndPayPayment.Button.Submit"),
                                                `${stepModel.paymentDetails.currency ?? "€"}${props.paymentDetails.totalAmount.toFixed(2) ?? "0.00"}`
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReviewAndPayPaymentStep;